import { SellerCatalogue } from "../catalogue/SellerCatalogue";
import { PeriodDefinitions } from "../catalogue/PeriodDefinitions";
import { PeopleManagement } from "../people/PeopleManagement";
import { RegistrationPolicy } from "../components/RegistrationPolicy";
import { ReportList } from "../reports/ReportList";
import { reportReason } from "../reports/reasons";
import { OrganizationManagement } from "../organizations/OrganizationManagement";
import { AuditList } from "../components/AuditList";
import { OrganizationSwitcher } from "../components/OrganizationSwitcher";
import { QualificationInbox } from "../qualification/QualificationInbox";
import { QualificationCount } from "../qualification/QualificationCount";
import { AppSelect } from "../components/inputs/AppSelect";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, useLocation } from "react-router-dom";
import {
  Squares2X2Icon,
  UsersIcon,
  FlagIcon,
  DocumentCheckIcon,
  ClockIcon,
  ArrowRightStartOnRectangleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ArrowUpRightIcon,
  Bars3Icon,
  XMarkIcon,
  CheckBadgeIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  BuildingLibraryIcon,
  CubeIcon,
  ShoppingBagIcon,
  ClipboardDocumentCheckIcon,
  BeakerIcon,
} from "@heroicons/react/24/outline";
import { Brand, LanguageButton } from "../components/Brand";
import { ActionDialog, type DialogAction } from "../components/ActionDialog";
import { useLocale } from "../i18n";
import { ProductManagement } from "../products/ProductManagement";
import { Products } from "../components/Products";
import { Managers } from "../components/Managers";
import { EventWorkspace } from "../events/EventWorkspace";
import { FinanceWorkspace } from "../finance/FinanceWorkspace";
import { ContentInspection } from "../components/ContentInspection";
import { can, canOpenFinance, canOpenService, landing } from "../auth/policy";
import type {
  Admin,
  AdminGateway,
  Permission,
  Workspace,
  SupportCase,
} from "../domain";

const MessageSimulation = __MZN_SIMULATION_TOOLS__ ? lazy(() => import("../simulation/MessageSimulation")) : null;

export function WorkspaceView({
  admin,
  gateway,
  preview = false,
  onSignOut,
  onOrganizationChange,
  switchingOrganization = false,
}: {
  admin: Admin;
  gateway: AdminGateway;
  preview?: boolean;
  onSignOut: () => Promise<void>;
  onOrganizationChange?: (id: string) => Promise<void>;
  switchingOrganization?: boolean;
}) {
  const { w, lang } = useLocale();
  const location = useLocation();
  const section =
    location.pathname.split("/").filter(Boolean).at(-1) || "dashboard";
  const current = section === "preview" ? "dashboard" : section;
  const base = preview ? "/preview" : "/app";
  const [data, setData] = useState<Workspace | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [menu, setMenu] = useState(false);
  const sidebar = useRef<HTMLElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);
  const searchField = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState<DialogAction | null>(null);
  const [inspected, setInspected] = useState<Map<string, number>>(new Map());
  const [notice, setNotice] = useState("");
  const legacy = can(admin, "moderation.read") && ["dashboard", "customers", "cases", "content", "audit"].includes(current);
  const reload = useCallback(async () => {
    if (!legacy) return;
    setBusy(true);
    setError("");
    try {
      const result = await gateway.load();
      setData(result);
    } catch {
      setError("load");
      throw new Error("Load failed");
    } finally {
      setBusy(false);
    }
  }, [gateway, legacy]);
  useEffect(() => {
    const controller = new AbortController();
    if (!legacy) { setData(null); setError(""); return () => controller.abort(); }
    gateway
      .load(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setData(result);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("load");
      });
    return () => controller.abort();
  }, [gateway, legacy]);
  useEffect(() => {
    setQuery("");
    setStatus("all");
    setMenu(false);
    setNotice("");
  }, [current, lang]);
  useEffect(() => {
    if (!menu) return;
    const root = sidebar.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    root?.querySelector<HTMLButtonElement>("button")?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); setMenu(false); }
      if (event.key !== "Tab") return;
      const items = Array.from(root?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled)') || []).filter(item => item.getClientRects().length);
      const first = items[0], last = items.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    const resize = () => { if (window.innerWidth > 800) setMenu(false); };
    document.addEventListener("keydown", keydown);
    window.addEventListener("resize", resize);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", keydown);
      window.removeEventListener("resize", resize);
      menuButton.current?.focus();
    };
  }, [menu]);
  const navigation = [
    ...(__MZN_SIMULATION_TOOLS__ && !preview ? [{key:"messageSimulation", title:w("محاكاة الرسائل والروابط", "Message & link testing"), icon:BeakerIcon, permission:"organizations.manage"}] as const : []),
    { key: "products", title: w("منتجات مزن", "MZN products"), icon: ShoppingBagIcon, permission: "products.read" },
    ...(!preview ? [
      { key: "sellerProducts", title: w("منتجات المنشأة", "Organization products"), icon: CubeIcon, permission: "sellerProducts.manage" },
      { key: "subscriptionPeriods", title: w("فترات الاشتراك", "Subscription periods"), icon: ClockIcon, permission: "products.manage" },
      { key: "productManagement", title: w("إدارة المنتجات", "Product management"), icon: CubeIcon, permission: "products.manage" },
      { key: "finance", title: w("الإدارة المالية", "Finance"), icon: BuildingLibraryIcon, permission: "finance.read" },
      { key: "registrationPolicy", title: w("إعدادات التسجيل", "Registration settings"), icon: ShieldCheckIcon, permission: "access.manage" },
      { key: "people", title: w("إدارة الأشخاص", "People management"), icon: UsersIcon, permission: "organizations.manage" },
      { key: "organizationManagement", title: w("إدارة المنشآت", "Organization management"), icon: BuildingLibraryIcon, permission: "organizations.manage" },
      { key: "qualifications", title: w("طلبات التأهيل", "Qualification requests"), icon: ClipboardDocumentCheckIcon, permission: "qualification.review" },
      { key: "access", title: w("المديرون والصلاحيات", "Managers & access"), icon: ShieldCheckIcon, permission: "access.manage" },
    ] as const : []),
    {
      key: "dashboard",
      title: w("نظرة عامة", "Overview"),
      icon: Squares2X2Icon,
      permission: "moderation.read",
    },
    {
      key: "customers",
      title: w("العملاء", "Customers"),
      icon: UsersIcon,
      permission: "crm.read",
    },
    {
      key: "cases",
      title: w("البلاغات", "Reports"),
      icon: FlagIcon,
      permission: "moderation.read",
    },
    {
      key: "content",
      title: w("مراجعة المحتوى", "Content review"),
      icon: DocumentCheckIcon,
      permission: "moderation.read",
    },
    {
      key: "audit",
      title: w("سجل الإجراءات", "Activity log"),
      icon: ClockIcon,
      permission: "audit.read",
    },
  ] as const;
  const active = navigation.find((n) => n.key === current);
  const servicePage = current === "events";
  const allowed = servicePage ? !preview && canOpenService(admin, location.search) : current === "finance" ? !preview && canOpenFinance(admin) : !!active && can(admin, active.permission as Permission);
  const navigationSection = servicePage ? "products" : current;
  const navGroups = admin.scope === "MZN_PLATFORM" ? [
    { title: w("مساحة العمل", "Workspace"), keys: ["dashboard", "products", "sellerProducts", "finance"] },
    { title: w("المجتمع والدعم", "Community & support"), keys: ["customers", "cases", "content"] },
    { title: w("الإدارة", "Administration"), keys: ["registrationPolicy", "productManagement", "subscriptionPeriods", "organizationManagement", "people", "qualifications", "access", "audit", ...(__MZN_SIMULATION_TOOLS__ ? ["messageSimulation"] : [])] },
  ] : [{ title: w("مساحة جهتك", "Your organization"), keys: ["products", "sellerProducts", "finance"] }];
  const descriptions: Record<string, string> = {
    dashboard: w("ابدأ بالأولويات، أو انتقل إلى الخدمة التي تريد إدارتها.", "Start with your priorities or open the service you want to manage."),
    products: w("خدماتك المشترَك بها ومنتجات مزن المتاحة لجهتك، في مكان واحد.", "Your subscribed services and MZN products, all in one place."),
    finance: w("إعدادات جهتك كبائع: طرق الدفع والهيئات الضريبية والتفويض المالي. وهي مستقلة عن شراء خدمات مزن.", "Your organization's seller settings: payment methods, tax authorities and finance access. Separate from purchasing MZN services."),
    events: w("أنشئ المسابقة، جهّز مواسمها، وتابع التصويت والنتائج خطوة بخطوة.", "Create a competition, prepare its seasons, and follow voting and results step by step."),
    sellerProducts: w("أدر منتجات منشأتك وفتراتها وأسعارها وعروضها.", "Manage your organization’s products, periods, prices and offers."),
    subscriptionPeriods: w("حدّد الفترات المتاحة لمزوّدي المنتجات عند تعريف الاشتراكات.", "Define the periods providers may select for subscription products."),
    productManagement: w("أدر منتجات مزن المعروضة للبيع وأسعارها ومدد الاشتراك.", "Manage MZN products for sale, prices and subscription terms."),
    ...(__MZN_SIMULATION_TOOLS__ ? {messageSimulation:w("عاين الدعوات والرموز واختبر وصول الروابط إلى هاتف المختبِر.","Preview invitations and codes and test links on a tester’s phone.")} : {}),
    people: w("دعوات مستقلة وإدارة حسابات الأشخاص في مزن.", "Independent invitations and people account management."),
    organizationManagement: w("أنشئ المنشآت وحدّد أهليتها ومديريها وصلاحياتهم.","Create organizations and define qualification and Manager permissions."),
    qualifications: w("راجع أهلية الجهات واعتمد تصنيفها المؤسسي قبل تمكين دخول مديريها.", "Review party eligibility and approve organization classifications before Manager admission."),
    access: w("تابع من يستطيع إدارة المنصة، وسجّل سبب كل تغيير في الصلاحيات.", "Manage who can administer the platform and record the reason for every access change."),
    customers: w("ابحث عن حساب عميل وراجع حالته قبل اتخاذ أي إجراء.", "Find a customer account and review its status before taking action."),
    cases: w("راجع البلاغات بحسب الحالة، بدءاً بالأحدث.", "Review reports by status, starting with the newest."),
    content: w("افحص المحتوى المرسل، ثم اعتمد قرار المراجعة مع توثيق السبب.", "Inspect submitted content, then record your review decision and reason."),
    audit: w("تتبّع الإجراءات والقرارات المسجّلة، بدءاً بالأحدث.", "Follow recorded actions and decisions, starting with the most recent."),
  };
  useEffect(() => {
    document.title = `${servicePage ? w("إدارة الخدمة", "Manage service") : active?.title || w("مركز الإدارة", "Admin workspace")} | MZN`;
  }, [active?.title, servicePage, w]);
  const pending = data?.cases.filter((c) => c.status !== "resolved") ?? [];
  const overdue = pending.filter(
    (c) => new Date(c.dueAt).getTime() < Date.now(),
  );
  const date = (iso: string) =>
    new Intl.DateTimeFormat(lang, {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  const statusLabel = (s: string) =>
    ({
      open: w("جديد", "New"),
      investigating: w("قيد المتابعة", "In progress"),
      resolved: w("تم الحل", "Resolved"),
      active: w("نشط", "Active"),
      suspended: w("معلّق", "Suspended"),
      pending: w("بانتظار المراجعة", "Pending review"),
      approved: w("معتمد", "Approved"),
      rejected: w("مرفوض", "Rejected"),
    })[s] || s;
  const queryMatches = (...values: string[]) =>
    values
      .join(" ")
      .toLocaleLowerCase()
      .includes(query.trim().toLocaleLowerCase());
  const hasFilters = Boolean(query.trim()) || status !== "all";
  const availableRecords = !data ? 0 : current === "dashboard" ? pending.length : current === "customers" ? data.customers.length : current === "content" ? data.content.length : data.cases.length;
  const emptyResults = <div className="collection-empty" role="status">
    <span className="collection-empty-icon">{hasFilters ? <MagnifyingGlassIcon /> : current === "customers" ? <UsersIcon /> : <CheckBadgeIcon />}</span>
    <h3>{hasFilters ? w("لا توجد نتائج لهذا البحث", "No results for this search") : current === "customers" ? w("لا توجد حسابات لعرضها بعد", "No customer accounts to display yet") : current === "content" ? w("لا يوجد محتوى للمراجعة حالياً", "No content to review right now") : w("لا توجد بلاغات تحتاج المتابعة حالياً", "No requests need attention right now")}</h3>
    <p>{hasFilters ? w("جرّب عبارة أخرى أو أزل التصفية لعرض القائمة كاملة.", "Try another phrase or clear the filters to see the full list.") : current === "customers" ? w("ستظهر هنا الحسابات المتاحة لك عند إضافتها إلى المنصة.", "Accounts available to you will appear here as they join the platform.") : w("ستظهر العناصر الجديدة هنا عند وصولها. يمكنك متابعة بقية أعمالك.", "New items will appear here when they arrive. You can continue with your other work.")}</p>
    {hasFilters && <button className="secondary" type="button" onClick={() => { setQuery(""); setStatus("all"); requestAnimationFrame(() => (searchField.current || document.getElementById("main"))?.focus()); }}>{w("مسح البحث والتصفية", "Clear search and filters")}</button>}
    {!hasFilters && current === "dashboard" && <NavLink className="secondary" to={`${base}/products`}>{w("انتقل إلى منتجات مزن", "Go to MZN products")}</NavLink>}
  </div>;
  function openCase(item: SupportCase) {
    setAction({
      title: `${item.reportNumber || item.id} · ${reportReason(item.subject, lang)}`,
      description: [item.details, ...item.notes].join("\n\n"),
      label:
        item.status === "open"
          ? w("بدء المتابعة", "Start review")
          : w("إغلاق البلاغ", "Resolve report"),
      run: (note, requestId) =>
        gateway.resolve({
          caseId: item.id,
          version: item.version ?? 0,
          requestId,
          status: item.status === "open" ? "investigating" : "resolved",
          note,
        }),
    });
  }
  async function updated() {
    await reload();
    setNotice(
      preview
        ? w(
            "تم تحديث البيانات التجريبية محلياً فقط.",
            "Only the local preview data was updated.",
          )
        : w("تم تحديث البيانات.", "Data updated."),
    );
  }
  if (!allowed) return <Navigate to={`${base}/products`} replace />;
  return (
    <div className="workspace">
      <a className="skip-link" href="#main">
        {w("انتقل للمحتوى", "Skip to content")}
      </a>
      {menu && (
        <button
          className="nav-backdrop"
          aria-label={w("إغلاق القائمة", "Close navigation")}
          onClick={() => setMenu(false)}
        />
      )}
      <aside ref={sidebar} id="workspace-navigation" className={"sidebar" + (menu ? " open" : "")}>
        <div className="sidebar-brand">
          <Brand home={preview ? "/preview/dashboard" : landing(admin)} />
          <button
            className="icon-button mobile-only"
            onClick={() => setMenu(false)}
            aria-label={w("إغلاق القائمة", "Close menu")}
          >
            <XMarkIcon />
          </button>
        </div>
        {!preview && admin.organization && onOrganizationChange && <OrganizationSwitcher current={admin.organization} onChange={onOrganizationChange} busy={switchingOrganization} />}
        <nav inert={switchingOrganization} aria-label={w("التنقل الرئيسي", "Main navigation")}>
          {navGroups.map(group => <div className="nav-group" key={group.title}>
            <p className="nav-group-title">{group.title}</p>
            {group.keys.map(key => navigation.find(n => n.key === key)).filter(n => n && (n.key === "finance" ? canOpenFinance(admin) : can(admin, n.permission)))
            .map((n) => (
              n &&
              <NavLink
                key={n.key}
                to={`${base}/${n.key}`}
                className={"nav-item" + (navigationSection === n.key ? " selected" : "")}
                aria-current={current === n.key ? "page" : undefined}
              >
                <n.icon aria-hidden="true" />
                <span>{n.title}</span>
                {n.key === "qualifications" && !preview && !switchingOrganization && <QualificationCount key={admin.organization?.id || admin.id} />}
                {n.key === "cases" && pending.length > 0 && (
                  <b>{pending.length}</b>
                )}
              </NavLink>
            ))}
          </div>)}
        </nav>
        <div className="sidebar-bottom">
          <span className="avatar" aria-hidden="true">{admin.name.charAt(0) || "M"}</span>
          <div>
            <strong title={preview ? undefined : admin.name}>
              <bdi>{preview ? w("مسؤول المعاينة", "Preview admin") : admin.name}</bdi>
            </strong>
            <small>
              {preview
                ? w("جلسة محلية تجريبية", "LOCAL PREVIEW SESSION")
                : admin.organization ? w(`تدير الآن: ${admin.organization.name}`, `Managing: ${admin.organization.name}`) : admin.scope === "MZN_PLATFORM" ? w("مدير منصة مزن", "MZN PLATFORM MANAGER") : w("مدير جهة · المنتجات", "ORGANIZATION MANAGER")}
            </small>
          </div>
          <button
            className="icon-button"
            aria-label={w("تسجيل الخروج", "Sign out")}
            onClick={() => {
              void onSignOut().catch(() =>
                setNotice(
                  w(
                    "تعذر إنهاء جلسة الخادم. حاول مجدداً.",
                    "Server sign-out failed. Please retry.",
                  ),
                ),
              );
            }}
          >
            <ArrowRightStartOnRectangleIcon />
          </button>
        </div>
      </aside>
      <div className="workspace-body" inert={menu || switchingOrganization} aria-busy={switchingOrganization}>
        <header className="workspace-header">
          <div className="breadcrumb">
            <button
              ref={menuButton}
              className="icon-button mobile-only"
              aria-label={w("فتح القائمة", "Open menu")}
              aria-expanded={menu}
              aria-controls="workspace-navigation"
              onClick={() => setMenu(true)}
            >
              <Bars3Icon />
            </button>
            <NavLink to={`${base}/${admin.scope === "MZN_PLATFORM" ? "dashboard" : "products"}`}>{admin.organization?.name || w("مساحة العمل", "Workspace")}</NavLink>
            <ChevronLeftIcon className="directional" />
            {servicePage && <><NavLink to={`${base}/products`}>{w("منتجات مزن", "MZN products")}</NavLink><ChevronLeftIcon className="directional" /></>}
            <strong>{servicePage ? w("إدارة الخدمة", "Manage service") : active?.title || w("الصفحة", "Page")}</strong>
          </div>
          <div className="header-actions">
            <span className="env-chip">{import.meta.env.VITE_APP_ENV}</span>
            <LanguageButton />
          </div>
        </header>
        {preview && (
          <div className="preview-banner">
            <span className="preview-dot" />
            <span>
              {w(
                "معاينة محلية — جميع الأسماء والأرقام تجريبية. لا تؤثر الإجراءات على أي حساب حقيقي.",
                "LOCAL PREVIEW — All names and numbers are fictional. Actions do not affect real accounts.",
              )}
            </span>
          </div>
        )}
        <main id="main" tabIndex={-1}>
          <div className={current === "events" ? "sr-only" : "page-heading"}>
            <div>
              <h1>
                {current === "dashboard"
                  ? w("أهلاً بفريق مزن", "Welcome, MZN team")
                  : servicePage ? w("إدارة الخدمة", "Manage service") : active?.title || w("صفحة غير موجودة", "Page not found")}
              </h1>
              <p className="muted">
                {descriptions[current]}
              </p>
            </div>
            {legacy && <button
              className="secondary refresh"
              disabled={busy}
              onClick={() => void reload().catch(() => undefined)}
            >
              <ArrowPathIcon className={busy ? "spin" : ""} />
              {w("تحديث", "Refresh")}
            </button>}
          </div>
          {notice && (
            <div className="success-box" role="status">
              {notice}
            </div>
          )}
          {switchingOrganization ? <div className="empty-state" role="status"><ArrowPathIcon className="spin" />{w("جارٍ الانتقال إلى المنشأة…", "Switching organization…")}</div> : !allowed ? (
            <div className="empty-state">
              <ShieldCheckIcon />
              <h2>
                {w(
                  "لا تملك صلاحية عرض هذه الصفحة",
                  "You do not have access to this page",
                )}
              </h2>
            </div>
          ) : current === "sellerProducts" ? <SellerCatalogue key={admin.organization?.id} /> : current === "subscriptionPeriods" ? <PeriodDefinitions /> : current === "productManagement" ? <ProductManagement organizationId={admin?.organization?.id} canReadFinance={admin?.permissions.includes("finance.read")} canManageFinance={admin?.permissions.includes("finance.manage")} /> : current === "products" ? <Products preview={preview} admin={admin} /> : current === "finance" ? <FinanceWorkspace admin={admin} /> : __MZN_SIMULATION_TOOLS__ && current === "messageSimulation" && MessageSimulation ? <Suspense fallback={<p>{w("جارٍ التحميل…","Loading…")}</p>}><MessageSimulation /></Suspense> : current === "registrationPolicy" ? <RegistrationPolicy /> : current === "people" ? <PeopleManagement /> : current === "organizationManagement" ? <OrganizationManagement /> : current === "qualifications" ? <QualificationInbox /> : current === "access" ? <Managers admin={admin} /> : current === "events" ? <EventWorkspace admin={admin} /> : error ? (
            <div className="empty-state">
              <ExclamationTriangleIcon />
              <h2>{w("تعذر تحميل البيانات", "Unable to load data")}</h2>
              <p>
                {w(
                  "تحقق من توفر خدمة الإدارة ثم أعد المحاولة.",
                  "Check that the admin service is available, then try again.",
                )}
              </p>
              <button
                className="secondary"
                onClick={() => void reload().catch(() => undefined)}
              >
                {w("إعادة المحاولة", "Try again")}
              </button>
            </div>
          ) : !data ? (
            <div className="empty-state" role="status">
              <ArrowPathIcon className="spin" />
              {w("جارٍ تحميل مساحة العمل…", "Loading workspace…")}
            </div>
          ) : (
            <>
              {current === "dashboard" && (
                <>
                  <div className="workspace-shortcuts">
                    <NavLink to={`${base}/products`} className="workspace-shortcut"><span className="shortcut-icon"><Squares2X2Icon /></span><div><strong>{w("إدارة المنتجات والخدمات", "Products & services")}</strong><p>{w("افتح خدمة مشتراة أو استعرض المنتجات المتاحة", "Open a purchased service or browse available products")}</p></div><ChevronLeftIcon className="directional" /></NavLink>
                  </div>
                  <div className="section-caption"><h2>{w("متابعة المجتمع", "Community activity")}</h2><span>{w("بحسب البيانات المحمّلة", "Based on loaded records")}</span></div>
                  <div className="stats-grid">
                    {[
                      [
                        w("بلاغات تحتاج المتابعة", "Open reports"),
                        pending.length,
                        w("بلاغات لم تُغلق بعد", "Unresolved reports"),
                        FlagIcon,
                        "brand-blue",
                      ],
                      [
                        w("بانتظار مراجعة المحتوى", "Awaiting review"),
                        data.content.filter((c) => c.status === "pending")
                          .length,
                        w(
                          "قبل ظهورها للمجتمع",
                          "Before reaching the community",
                        ),
                        DocumentCheckIcon,
                        "purple",
                      ],
                      [
                        w("تجاوزت موعد المتابعة", "Overdue reports"),
                        overdue.length,
                        w("تحتاج اهتماماً الآن", "Need attention now"),
                        ClockIcon,
                        "red",
                      ],
                      [
                        w("بلاغات تم حلّها", "Resolved reports"),
                        data.cases.filter((c) => c.status === "resolved")
                          .length,
                        w("ضمن الحالات المعروضة", "Within the loaded cases"),
                        CheckBadgeIcon,
                        "green",
                      ],
                    ].map(([title, value, subtitle, Icon, color]) => {
                      const StatIcon = Icon as typeof FlagIcon;
                      return (
                        <article className="stat-card" key={String(title)}>
                          <div className="stat-top">
                            <span>{String(title)}</span>
                            <span className={"stat-icon " + color}>
                              <StatIcon />
                            </span>
                          </div>
                          <strong>{new Intl.NumberFormat(lang).format(Number(value))}</strong>
                          <small>{String(subtitle)}</small>
                        </article>
                      );
                    })}
                  </div>
                  <div className="attention-strip">
                    <div className="attention-icon">
                      <ClockIcon />
                    </div>
                    <div>
                      <strong>
                        {w(
                          pending.length ? "بلاغات تحتاج المتابعة" : "لا توجد بلاغات مفتوحة في القائمة الحالية",
                          pending.length ? "Reports need your attention" : "No open reports in the current list",
                        )}
                      </strong>
                      <p>
                        {pending.length ? w(
                          `هناك ${overdue.length} بلاغ متأخر و${pending.filter((c) => !overdue.includes(c)).length} بلاغ آخر ينتظر فريقك.`,
                          `${overdue.length} overdue report(s) and ${pending.filter((c) => !overdue.includes(c)).length} other report(s) are waiting for your team.`,
                        ) : w("ستظهر البلاغات الجديدة هنا عند وصولها.", "New reports will appear here when they arrive.")}
                      </p>
                    </div>
                    <NavLink className="text-link" to={`${base}/cases`}>
                      {w("متابعة البلاغات", "Review reports")}
                      <ArrowUpRightIcon />
                    </NavLink>
                  </div>
                </>
              )}
              {["dashboard", "cases", "customers", "content"].includes(
                current,
              ) && (
                <section className="panel">
                  <div className="panel-header">
                    <div>
                      <h2>
                        {current === "dashboard"
                          ? w("الأولوية الآن", "Your priority queue")
                          : current === "cases" ? w("قائمة البلاغات", "Report inbox") : active?.title}
                      </h2>
                      <p>
                        {current === "dashboard"
                          ? w(
                              "البلاغات المفتوحة، بدءاً بالأحدث.",
                              "Open reports, newest first.",
                            )
                          : w(
                              "ابحث وصفِّ النتائج للوصول سريعاً.",
                              "Search and filter to find what you need.",
                            )}
                      </p>
                    </div>
                    <span className="subtle-tag">
                      {w("مساحة الفريق", "TEAM INBOX")}
                    </span>
                  </div>
                  {!["dashboard", "cases"].includes(current) && (availableRecords > 0 || hasFilters) && <div className="table-toolbar">
                    <div className="search-field">
                      <MagnifyingGlassIcon />
                      <input
                        ref={searchField}
                        aria-label={w("بحث في النتائج", "Search results")}
                        placeholder={w(
                          "ابحث بالاسم أو رقم الطلب…",
                          "Search by name or case ID…",
                        )}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                      />
                    </div>
                    {current !== "dashboard" && (
                      <AppSelect
                        aria-label={w("تصفية حسب الحالة", "Filter by status")}
                        value={status}
                        onChange={(e) => setStatus(e)}
                      >
                        <option value="all">
                          {w("كل الحالات", "All statuses")}
                        </option>
                        {(current === "customers"
                          ? ["active", "suspended"]
                          : current === "content"
                            ? ["pending", "approved", "rejected"]
                            : ["open", "investigating", "resolved"]
                        ).map((s) => (
                          <option value={s} key={s}>
                            {statusLabel(s)}
                          </option>
                        ))}
                      </AppSelect>
                    )}
                  </div>}
                  {["dashboard", "cases"].includes(current) && <ReportList key={current} gateway={gateway} dashboard={current === "dashboard"}
                    refresh={data} canReview={can(admin, "moderation.resolve")} openCase={openCase} date={date} statusLabel={statusLabel} />}
                  {current === "customers" &&
                    (() => {
                      const items = data.customers.filter(
                        (c) =>
                          (status === "all" || c.status === status) &&
                          queryMatches(c.name, c.id, c.email),
                      );
                      if (!items.length) return emptyResults;
                      return (
                        <div className="table-scroll">
                          <table>
                            <thead>
                              <tr>
                                {[
                                  w("العميل", "Customer"),
                                  w("التواصل", "Contact"),
                                  w("الحالة", "Status"),
                                  w("تاريخ الانضمام", "Joined"),
                                  w("الإجراء", "Action"),
                                ].map((h) => (
                                  <th key={h}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((c) => (
                                <tr key={c.id}>
                                  <td>
                                    <div className="person">
                                      <span className="avatar">
                                        {c.initials}
                                      </span>
                                      <div>
                                        <strong>{c.name}</strong>
                                        <details className="record-technical"><summary>{w("معرّف الحساب", "Account ID")}</summary><bdi>{c.id}</bdi></details>
                                      </div>
                                    </div>
                                  </td>
                                  <td dir="ltr">{c.email}</td>
                                  <td>
                                    <span className={"badge " + c.status}>
                                      {statusLabel(c.status)}
                                    </span>
                                  </td>
                                  <td className="muted">{date(c.joinedAt)}</td>
                                  <td>
                                    <button
                                      className="text-button danger-text"
                                      disabled={
                                        c.status === "suspended" ||
                                        !can(admin, "users.suspend")
                                      }
                                      onClick={() =>
                                        setAction({
                                          title: w(
                                            "تعليق الحساب",
                                            "Suspend account",
                                          ),
                                          description: preview
                                            ? w(
                                                `سيُعلّق حساب ${c.name} التجريبي محلياً فقط.`,
                                                `Only ${c.name}’s fictional preview account will be changed.`,
                                              )
                                            : w(
                                                "سيُمنع الحساب من الدخول وتُبطل جلساته. يلزم توثيق سبب القرار.",
                                                "This blocks sign-in and revokes all sessions. Record the reason for the decision.",
                                              ),
                                          label: w(
                                            "تأكيد التعليق",
                                            "Confirm suspension",
                                          ),
                                          danger: true,
                                          run: (reason, requestId) =>
                                            gateway.suspendCustomer(
                                              c.id,
                                              reason,
                                              requestId,
                                            ),
                                        })
                                      }
                                    >
                                      {w("تعليق الحساب", "Suspend")}
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  {current === "content" && (
                    <div className="content-grid">
                      {data.content
                        .filter(
                          (c) =>
                            (status === "all" || c.status === status) &&
                            queryMatches(c.author, c.text, c.id),
                        )
                        .map((c) => (
                          <article className="content-card" key={c.id}>
                            <div className="content-top">
                              <span className="avatar mini">
                                {c.author.charAt(0)}
                              </span>
                              <strong>{c.author}</strong>
                              <span className={"badge " + c.status}>
                                {statusLabel(c.status)}
                              </span>
                            </div>
                            <p>{c.text}</p>
                            <small className="muted">
                              {date(c.submittedAt)}
                            </small>
                            {c.requiresInspection && gateway.details && <ContentInspection id={c.id} gateway={gateway} onInspected={(value, version) => setInspected(previous => { const next = new Map(previous); if (value) next.set(c.id, version); else next.delete(c.id); return next; })} />}
                            {c.status === "pending" && (
                              <div className="content-actions">
                                {(["approved", "rejected"] as const).map(
                                  (decision) => (
                                    <button
                                      key={decision}
                                      className={
                                        decision === "approved"
                                          ? "secondary"
                                          : "text-button danger-text"
                                      }
                                      disabled={
                                        !can(admin, "moderation.resolve") ||
                                        (c.requiresInspection ? !inspected.has(c.id) : c.kind === "reel")
                                      }
                                      onClick={() =>
                                        setAction({
                                          title:
                                            decision === "approved"
                                              ? w(
                                                  "اعتماد المحتوى",
                                                  "Approve content",
                                                )
                                              : w(
                                                  "رفض المحتوى",
                                                  "Reject content",
                                                ),
                                          description: c.text,
                                          label: w(
                                            "تأكيد القرار",
                                            "Confirm decision",
                                          ),
                                          danger: decision === "rejected",
                                          run: (reason, requestId) =>
                                            gateway.review(
                                              c.id,
                                              decision,
                                              reason,
                                              requestId,
                                              inspected.get(c.id),
                                            ),
                                        })
                                      }
                                    >
                                      {decision === "approved"
                                        ? w("اعتماد", "Approve")
                                        : w("رفض", "Reject")}
                                    </button>
                                  ),
                                )}
                              </div>
                            )}
                          </article>
                        ))}
                      {!data.content.some(
                        (c) =>
                          (status === "all" || c.status === status) &&
                          queryMatches(c.author, c.text, c.id),
                      ) && (
                        emptyResults
                      )}
                    </div>
                  )}
                  <div className="panel-footer">
                    <span>
                      {preview
                        ? w(
                            "بيانات توضيحية · لا توجد بيانات عملاء حقيقية",
                            "ILLUSTRATIVE DATA · NO REAL CUSTOMER INFORMATION",
                          )
                        : w(
                            "البيانات المتاحة لحسابك",
                            "Data available to your account",
                          )}
                    </span>
                    <ShieldCheckIcon />
                  </div>
                </section>
              )}
              {current === "audit" && (
                <section className="panel">
                  <div className="panel-header">
                    <div>
                      <h2>
                        {w(
                          "سجل واضح لكل قرار",
                          "An accountable record of every decision",
                        )}
                      </h2>
                      <p>
                        {w(
                          "عرض الإجراءات المسجلة بحسب الأحدث.",
                          "Recorded actions, with the newest first.",
                        )}
                      </p>
                    </div>
                    <span className="subtle-tag">{data.audit.length}</span>
                  </div>
                  <div>
                    <AuditList items={data.audit} date={date} />
                    {!data.audit.length && <div className="collection-empty" role="status">
                      <span className="collection-empty-icon"><ClockIcon /></span>
                      <h3>{w("لا توجد إجراءات مسجلة بعد", "No recorded actions yet")}</h3>
                      <p>{w("ستظهر هنا إجراءات الإدارة مع منفّذها ووقتها وسببها عند تسجيلها.", "Administrative actions will appear here with their actor, time, and reason as they are recorded.")}</p>
                    </div>}
                  </div>
                </section>
              )}
            </>
          )}
          <footer className="workspace-footer">
            <span>MZN · {w("مركز الإدارة", "Admin workspace")}</span>
            <span>
              {w(
                "تُعرض الأدوات بحسب صلاحياتك في المنشأة الحالية.",
                "Tools are available according to your access in the current organization.",
              )}
            </span>
          </footer>
        </main>
      </div>
      {action && (
        <ActionDialog
          action={action}
          onClose={() => setAction(null)}
          onSuccess={updated}
        />
      )}
    </div>
  );
}
